import type { ConfigId, Serialised } from '@squaredup/ids';
import type { Config, DataStreamDefinitionEntity, Plugin } from 'dynamo-wrapper';
import { usePlugin } from 'pages/settings/plugins/components/usePlugin';
import { useTenant } from 'queries/hooks/useTenant';
import { useDataSourceConfig } from '../useDataSourceConfig';
import { useOverviewDataStreams } from '../useOverviewDataStreams';

export interface SaveAsPluginDependencies {
    basePlugin?: Serialised<Plugin>;
    basePluginConfig?: Serialised<Config>;
    basePluginDataStreams?: Serialised<DataStreamDefinitionEntity>[];
    tenantId?: string;
}

/**
 * Get the dependencies required to save a plugin config as a plugin.
 */
export const useSaveAsPluginDependencies = (
    pluginId: string,
    pluginConfigId?: ConfigId['value']
): SaveAsPluginDependencies & { isLoading: boolean } => {
    const { data: plugin, isLoading: isPluginDataLoading } = usePlugin(pluginId);
    const { data: tenant, isLoading: isLoadingTenant } = useTenant();
    const { data: dataSourceConfigData, isLoading: isConfigLoading } = useDataSourceConfig(pluginConfigId);
    const { data: pluginDataStreams, isLoading: isLoadingDataStreams } = useOverviewDataStreams(
        pluginId,
        pluginConfigId
    );

    return {
        isLoading: isPluginDataLoading || isLoadingTenant || isLoadingDataStreams || isConfigLoading,
        basePlugin: plugin,
        tenantId: tenant?.id,
        basePluginConfig: dataSourceConfigData,
        basePluginDataStreams: pluginDataStreams
    };
};
